import type { ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import { Label } from "./label";

/** Props the field passes to its control so the label, hint and error are announced with it. */
export interface FieldControlProps {
  id: string;
  "aria-describedby"?: string;
  "aria-invalid"?: true;
  "aria-required"?: true;
}

/**
 * Figma Input field frame: label (Required → «*»), the control, and «Hint text» below, which the error replaces.
 * The control is a render prop, so it works with `register()` of react-hook-form:
 * `<FormField id="phone" label=… error=…>{(field) => <Input {...field} {...register("phone")} />}</FormField>`.
 */
export function FormField({
  id,
  label,
  required = false,
  hint,
  error,
  className,
  children,
}: {
  id: string;
  label: ReactNode;
  required?: boolean;
  hint?: ReactNode;
  /** Shown instead of the hint; marks the control invalid. */
  error?: ReactNode;
  className?: string;
  children: (control: FieldControlProps) => ReactNode;
}) {
  // `error={invalid && message}` passes false or "" when valid: any falsy error brings the hint back
  const isInvalid = Boolean(error);
  const note = isInvalid ? error : hint;
  const noteId = `${id}-note`;
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <Label htmlFor={id}>
        {label}
        {required && (
          <span aria-hidden className="text-danger">
            {" *"}
          </span>
        )}
      </Label>
      {children({
        id,
        "aria-describedby": note ? noteId : undefined,
        "aria-invalid": isInvalid ? true : undefined,
        "aria-required": required ? true : undefined,
      })}
      {note && (
        <p id={noteId} className={cn("text-body-sm", isInvalid ? "text-danger" : "text-ink-toned")}>
          {note}
        </p>
      )}
    </div>
  );
}
